import React, { useState, useEffect } from 'react';
import { socialMediaService } from '../../services/api';
import { 
  Plus, Search, Filter, Edit2, Trash2, 
  Facebook, Instagram, Youtube, Linkedin,
  X, Save, Loader2, Globe, TrendingUp, BarChart2,
  Calendar, CheckCircle, Clock, AlertCircle, Share2
} from 'lucide-react';
import XLogo from '../XLogo';

function SocialMediaManagement() {
  const [activeTab, setActiveTab] = useState('links'); // links, posts, campaigns
  const [links, setLinks] = useState({
    facebook: '',
    instagram: '',
    youtube: '',
    twitter: '',
    linkedin: '',
  });
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadInitialData();
  }, [activeTab]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'links') {
        const res = await socialMediaService.index();
        setLinks(res.data || links);
      } else if (activeTab === 'posts') {
        const res = await socialMediaService.indexPosts();
        setPosts(res.data || res || []);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLinksUpdate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await socialMediaService.update(links);
      alert('Social media links updated successfully!');
    } catch (error) {
      alert('Failed to update links');
    } finally {
      setSubmitting(false);
    }
  };

  const platformIcons = {
    facebook: <Facebook size={20} className="text-blue-500" />,
    instagram: <Instagram size={20} className="text-pink-500" />,
    youtube: <Youtube size={20} className="text-red-500" />,
    twitter: <XLogo size={20} className="text-sky-500" />,
    linkedin: <Linkedin size={20} className="text-blue-700" />,
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-100">Social Media Integration</h2>
          <p className="text-slate-400 text-sm">Manage platform connections and cross-platform visibility.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex bg-slate-900 border border-slate-800 rounded-xl p-1">
            <button
              onClick={() => setActiveTab('links')}
              className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${
                activeTab === 'links' ? 'bg-red-600 text-white shadow-lg' : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              Platform Links
            </button>
            <button
              onClick={() => setActiveTab('posts')}
              className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${
                activeTab === 'posts' ? 'bg-red-600 text-white shadow-lg' : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              Recent Posts
            </button>
          </div>
        </div>
      </div>

      {activeTab === 'links' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 animate-in fade-in duration-500">
          <div className="lg:col-span-2">
            <form onSubmit={handleLinksUpdate} className="bg-slate-900 border border-slate-800 rounded-3xl p-8 space-y-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2.5 bg-red-500/10 rounded-xl text-red-500">
                  <Share2 size={20} />
                </div>
                <div>
                  <h3 className="font-bold text-slate-100">Official Profiles</h3>
                  <p className="text-xs text-slate-500">Links shown in website header and footer.</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {Object.keys(links).map((platform) => (
                  <div key={platform}>
                    <label className="block text-sm font-semibold text-slate-300 mb-2 capitalize">{platform}</label>
                    <div className="flex items-center">
                      <div className="p-3 bg-slate-800 border border-r-0 border-slate-700 rounded-l-xl">
                        {platformIcons[platform]}
                      </div>
                      <input
                        type="url"
                        value={links[platform]}
                        onChange={(e) => setLinks({ ...links, [platform]: e.target.value })}
                        placeholder={`https://${platform}.com/ortusfnb`}
                        className="flex-1 bg-slate-950 border border-slate-700 rounded-r-xl px-4 py-3 text-sm text-slate-100 focus:outline-none focus:border-red-500 transition-colors"
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="pt-4 border-t border-slate-800 flex justify-end">
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-8 py-3 rounded-xl transition font-bold shadow-lg shadow-red-900/20 disabled:opacity-50"
                >
                  {submitting ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                  Save Social Settings
                </button>
              </div>
            </form>
          </div>

          <div className="space-y-6">
            <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6">
              <h3 className="text-sm font-black uppercase tracking-widest text-slate-100 mb-6 flex items-center gap-2">
                <TrendingUp size={16} className="text-blue-500" /> Engagement Stats
              </h3>
              <div className="space-y-4">
                {[
                  { label: 'Followers', value: '42.5K', growth: '+2.4%' },
                  { label: 'Avg. Reach', value: '150K', growth: '+12%' },
                  { label: 'Share Rate', value: '3.8%', growth: '+0.5%' },
                ].map((stat, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 rounded-2xl bg-slate-950/50 border border-slate-800">
                    <p className="text-xs font-bold text-slate-400">{stat.label}</p>
                    <div className="text-right">
                      <p className="text-sm font-black text-slate-100">{stat.value}</p>
                      <p className="text-[10px] text-red-500 font-bold">{stat.growth}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6">
              <h3 className="text-sm font-black uppercase tracking-widest text-slate-100 mb-4 flex items-center gap-2">
                <Globe size={16} className="text-red-500" /> Integration Status
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between text-xs py-2">
                  <span className="text-slate-400">Open Graph Tags</span>
                  <span className="text-red-500 font-bold">Enabled</span>
                </div>
                <div className="flex items-center justify-between text-xs py-2">
                  <span className="text-slate-400">Twitter Cards</span>
                  <span className="text-red-500 font-bold">Enabled</span>
                </div>
                <div className="flex items-center justify-between text-xs py-2">
                  <span className="text-slate-400">API Sync</span>
                  <span className="text-amber-500 font-bold">Config Required</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'posts' && (
        <div className="bg-slate-900 border border-slate-800 rounded-3xl overflow-hidden animate-in slide-in-from-bottom-4 duration-500">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-slate-950/50 border-b border-slate-800">
                <tr>
                  <th className="px-6 py-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">Post Content</th>
                  <th className="px-6 py-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">Platform</th>
                  <th className="px-6 py-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">Engagement</th>
                  <th className="px-6 py-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-xs font-semibold text-slate-400 uppercase tracking-wider text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {loading ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center">
                      <Loader2 className="w-8 h-8 text-red-500 animate-spin mx-auto" />
                    </td>
                  </tr>
                ) : posts.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-20 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <Share2 className="text-slate-700" size={40} />
                        <p className="text-slate-500 text-sm">No social posts tracked yet.</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  posts.map((post) => (
                    <tr key={post.id} className="hover:bg-slate-800/30 transition-colors">
                      <td className="px-6 py-4">
                        <div className="max-w-xs">
                          <p className="text-sm text-slate-100 line-clamp-2">{post.content}</p>
                          <p className="text-[10px] text-slate-500 mt-1 font-medium">
                            {new Date(post.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <div className="p-1.5 bg-slate-800 rounded-lg">
                            {platformIcons[post.platform]}
                          </div>
                          <span className="text-xs font-bold text-slate-300 capitalize">{post.platform}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-4">
                          <div>
                            <p className="text-[10px] text-slate-500 font-black uppercase">Likes</p>
                            <p className="text-xs font-bold text-slate-100">{post.likes || 0}</p>
                          </div>
                          <div>
                            <p className="text-[10px] text-slate-500 font-black uppercase">Shares</p>
                            <p className="text-xs font-bold text-slate-100">{post.shares || 0}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${
                          post.status === 'published' ? 'bg-red-500/10 text-red-500 border border-red-500/20' : 'bg-amber-500/10 text-amber-500 border border-amber-500/20'
                        }`}>
                          {post.status === 'published' ? <CheckCircle size={10} /> : <Clock size={10} />}
                          {post.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button className="p-2 text-slate-400 hover:text-red-400 transition-colors">
                            <Globe size={18} />
                          </button>
                          <button className="p-2 text-slate-400 hover:text-red-400 transition-colors">
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default SocialMediaManagement;
